import dayjs from 'dayjs';

/**
 * Format optimal time for display
 * @param {Object} optimalTime - Optimal time object from API
 * @returns {string} - Formatted time string
 */
export const formatOptimalTime = (optimalTime) => {
  if (!optimalTime) return '';
  
  // If we have a pre-formatted time, use it
  if (optimalTime.time) {
    return optimalTime.time;
  }
  
  // Otherwise format from hour/minute
  const hour = optimalTime.hour || 0;
  const minute = optimalTime.minute || 0;
  
  return dayjs().hour(hour).minute(minute).format('h:mm A');
};

/**
 * Get confidence badge props based on confidence score
 * @param {number} confidenceScore - Confidence score (0-1)
 * @param {boolean} isFallback - Whether this is a fallback recommendation
 * @returns {Object} - Badge props { color, label }
 */
export const getConfidenceBadge = (confidenceScore, isFallback = false) => {
  if (isFallback) {
    return {
      color: 'default',
      label: 'Industry Standard',
    };
  }
  
  if (confidenceScore >= 0.8) {
    return {
      color: 'success',
      label: 'High Confidence',
    };
  } else if (confidenceScore >= 0.6) {
    return {
      color: 'primary',
      label: 'Good Confidence',
    };
  } else if (confidenceScore >= 0.4) {
    return {
      color: 'warning',
      label: 'Medium Confidence',
    };
  } else {
    return {
      color: 'error',
      label: 'Low Confidence',
    };
  }
};

/**
 * Create a scheduled date from optimal time suggestion
 * @param {Object} optimalTime - Optimal time object
 * @param {Date} baseDate - Base date to apply time to (defaults to today)
 * @returns {Date} - Scheduled date
 */
export const createScheduledDate = (optimalTime, baseDate = null) => {
  if (!optimalTime) return null;
  
  const base = baseDate || new Date();
  const hour = optimalTime.hour || 0;
  const minute = optimalTime.minute || 0;
  
  return dayjs(base)
    .hour(hour)
    .minute(minute)
    .second(0)
    .millisecond(0)
    .toDate();
};

/**
 * Get the next available optimal time (if current time has passed)
 * @param {Array} optimalTimes - Array of optimal time objects
 * @param {Date} baseDate - Base date to check against (defaults to now)
 * @returns {Object|null} - Next optimal time or null
 */
export const getNextOptimalTime = (optimalTimes, baseDate = null) => {
  if (!optimalTimes || optimalTimes.length === 0) return null;
  
  const now = baseDate || new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  
  // Find the next time that hasn't passed today
  const todayOptions = optimalTimes.filter(time => {
    const timeHour = time.hour || 0;
    const timeMinute = time.minute || 0;
    
    return timeHour > currentHour || (timeHour === currentHour && timeMinute > currentMinute);
  });
  
  // If we have options today, return the first one
  if (todayOptions.length > 0) {
    return todayOptions[0];
  }
  
  // Otherwise return the first option for tomorrow
  return optimalTimes[0];
};

/**
 * Sort optimal times by hour and minute
 * @param {Array} optimalTimes - Array of optimal time objects
 * @returns {Array} - Sorted array
 */
export const sortOptimalTimes = (optimalTimes) => {
  if (!optimalTimes || optimalTimes.length === 0) return [];
  
  return [...optimalTimes].sort((a, b) => {
    const aHour = a.hour || 0;
    const aMinute = a.minute || 0;
    const bHour = b.hour || 0;
    const bMinute = b.minute || 0;
    
    if (aHour !== bHour) {
      return aHour - bHour;
    }
    
    return aMinute - bMinute;
  });
};

/**
 * Check if a time is within optimal time windows
 * @param {Date} scheduledTime - Time to check
 * @param {Array} optimalTimes - Array of optimal time objects
 * @param {number} windowMinutes - Window in minutes around optimal time (default: 30)
 * @returns {boolean} - Whether time is optimal
 */
export const isWithinOptimalWindow = (scheduledTime, optimalTimes, windowMinutes = 30) => {
  if (!scheduledTime || !optimalTimes || optimalTimes.length === 0) return false;
  
  const scheduledHour = scheduledTime.getHours();
  const scheduledMinute = scheduledTime.getMinutes();
  
  return optimalTimes.some(optimalTime => {
    const optimalHour = optimalTime.hour || 0;
    const optimalMinute = optimalTime.minute || 0;
    
    // Convert to minutes for easier comparison
    const scheduledTotalMinutes = scheduledHour * 60 + scheduledMinute;
    const optimalTotalMinutes = optimalHour * 60 + optimalMinute;
    
    const difference = Math.abs(scheduledTotalMinutes - optimalTotalMinutes);
    
    return difference <= windowMinutes;
  });
};

/**
 * Get recommendation message based on type and data sufficiency
 * @param {string} recommendationType - Type of recommendation
 * @param {boolean} hasSufficientData - Whether user has sufficient data
 * @param {string} platform - Platform name
 * @returns {string} - Recommendation message
 */
export const getRecommendationMessage = (recommendationType, hasSufficientData, platform) => {
  if (recommendationType === 'error') {
    return 'Unable to load optimal times. Please try again later.';
  }
  
  if (!hasSufficientData) {
    return `Based on industry standards for ${platform}. Your personalized times will appear after more posts.`;
  }
  
  if (recommendationType === 'personalized') {
    return `Based on your ${platform} posting history and engagement patterns.`;
  }
  
  if (recommendationType === 'hybrid') {
    return `Based on your data combined with ${platform} best practices.`;
  }
  
  return `Industry standard times for ${platform}.`;
};

/**
 * Track optimal time usage for analytics
 * @param {Object} optimalTime - Selected optimal time
 * @param {string} platform - Platform name
 * @param {string} source - Source of selection (quick_schedule, manual, etc.)
 * @param {boolean} wasAccepted - Whether the user accepted the suggestion
 * @param {Date} actualPostTime - When the user actually posted (optional)
 * @param {Object} contextData - Additional context data
 */
export const trackOptimalTimeUsage = async (
  optimalTime, 
  platform, 
  source = 'quick_schedule',
  wasAccepted = true,
  actualPostTime = null,
  contextData = {}
) => {
  try {
    // Prepare tracking data
    const trackingData = {
      platform: platform.toLowerCase(),
      suggested_time: optimalTime.scheduledTime || new Date().toISOString(),
      suggestion_type: optimalTime.is_fallback ? 'industry' : 'personal',
      confidence_score: optimalTime.confidence_score || 0.5,
      was_accepted: wasAccepted,
      actual_post_time: actualPostTime ? actualPostTime.toISOString() : null,
      expected_engagement_rate: optimalTime.engagement_rate,
      experiment_group: null, // Could be used for A/B testing
      context_data: {
        source,
        confidence: optimalTime.confidence,
        reason: optimalTime.reason,
        post_count: optimalTime.post_count,
        ...contextData
      }
    };

    // Get auth token
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No auth token available for tracking');
      return;
    }

    // Send tracking data to API
    const response = await fetch('/api/analytics/optimal-times/tracking/usage/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Token ${token}`
      },
      body: JSON.stringify(trackingData)
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Optimal time usage tracked successfully:', result.tracking_id);
      return result.tracking_id;
    } else {
      const error = await response.json();
      console.warn('Failed to track optimal time usage:', error.message);
    }
    
  } catch (error) {
    console.warn('Failed to track optimal time usage:', error);
  }
};

/**
 * Get tracking metrics for optimal time suggestions
 * @param {string} platform - Platform to filter by (optional)
 * @param {number} days - Number of days to analyze (default: 30)
 * @param {string} suggestionType - Type of suggestion to filter by (optional)
 */
export const getOptimalTimeTrackingMetrics = async (platform = null, days = 30, suggestionType = null) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No auth token available');
    }

    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);
    if (days !== 30) params.append('days', days.toString());
    if (suggestionType) params.append('suggestion_type', suggestionType);

    const response = await fetch(`/api/analytics/optimal-times/tracking/metrics/?${params}`, {
      headers: {
        'Authorization': `Token ${token}`
      }
    });

    if (response.ok) {
      const result = await response.json();
      return result.metrics;
    } else {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get tracking metrics');
    }
  } catch (error) {
    console.error('Error getting tracking metrics:', error);
    throw error;
  }
};

/**
 * Get prediction accuracy analysis
 * @param {string} platform - Platform to filter by (optional)
 * @param {number} days - Number of days to analyze (default: 30)
 */
export const getPredictionAccuracyAnalysis = async (platform = null, days = 30) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No auth token available');
    }

    const params = new URLSearchParams();
    if (platform) params.append('platform', platform);
    if (days !== 30) params.append('days', days.toString());

    const response = await fetch(`/api/analytics/optimal-times/tracking/accuracy/?${params}`, {
      headers: {
        'Authorization': `Token ${token}`
      }
    });

    if (response.ok) {
      const result = await response.json();
      return result.analysis;
    } else {
      const error = await response.json();
      throw new Error(error.message || 'Failed to get prediction accuracy analysis');
    }
  } catch (error) {
    console.error('Error getting prediction accuracy analysis:', error);
    throw error;
  }
}; 